import { Typography } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import { ActionsCell } from '../../../Component/Common/Components/DataTable/ActionsCell';
import { DefaultCell } from '../../../Component/Common/Components/DataTable/DefaultCell';
import { ScorecardData } from '../../../Component/Model/Dto/ScorecardLicense';
import { DateFormatter } from '../../../Component/Model/helpers';
import { ActionType, TableColumn } from '../../../Component/Model/Table/Table';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../State/user.Atom';
import { SiteAdminRole, SubAdminRole, UserAdminRole } from '../../../Component/Common/Constants/UserRoles';
import { toast } from 'react-toastify';
import { isExpired } from '../../../Helpers/scorecardHelper';
import React from 'react';

interface ScorecardRows {
    ID: string;
    Name: ILinkProps;
    ScorecardType: string;
    CreatedDate: string;
    IsConsolidated: string;
    FromDate: string;
    ToDate: string;
    Actions: ActionType[];
}

interface ILinkProps {
    linkTo: string;
    itemName: React.ReactNode;
}

export interface ScorecardTable {
    columns: Array<TableColumn>;
    rows: Array<ScorecardRows>;
}

export function ScorecardTableMapper(dataObject: ScorecardData[], handleAssignUser: (id: string) => void): ScorecardTable {
    const userRoles = useRecoilValue(userRolesState);
    const history = useHistory();

    const handleLinkClick = (item: ScorecardData) => {
        if (isExpired(item.LicenseExpiryDate)) {
            toast.error('The scorecard license has expired. Please renew your license before you can edit');
            return;
        }
        history.push(`/ee/scorecard/${item.ID}/dashboard/EmployeeHeadcount`);
    };

    const columns = [
        { Header: 'Name', accessor: 'Name', Cell: ({ value }: any) => 
            <Typography variant="caption" fontWeight='regular' color="#007bff" component={Link} to={value.linkTo}>{value.itemName}</Typography> 
        },
        { Header: 'Scorecard Type', accessor: 'ScorecardType',id: "ScorecardTypeName", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Created Date', accessor: 'CreatedDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Consolidated Scorecard', accessor: 'IsConsolidated', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'From Date', accessor: 'FromDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'To Date', accessor: 'ToDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true }
    ];
    
    if (dataObject.length > 0) {
        const rows = dataObject.map((item: ScorecardData, index: number) => ({
            ID: item.ID,
            Name: { 
                linkTo: isExpired(item.LicenseExpiryDate) ? "#" : `/ee/scorecard/${item.ID}/dashboard/EmployeeHeadcount`, 
                itemName: (
                    <div style={{ cursor: "pointer" }} onClick={() => handleLinkClick(item)}>
                        {item.Name}
                    </div>
                )},
            ScorecardType: item.ScorecardType,
            CreatedDate: DateFormatter(item.CreatedDate.toString()),
            IsConsolidated: item.IsConsolidated ? 'Yes' : 'No',
            FromDate: DateFormatter(item.FromDate.toString()),
            ToDate: DateFormatter(item.ToDate.toString()),
            TotalPoints: item.TotalPoints,
            RatingLevel: item.RatingLevel,
            AdjustedLevel: item.AdjustedLevel,
            Actions: getActionsColumn(item, index)
        }));
        
        return { columns: columns, rows: rows };
    }
    else {
        return { columns: columns, rows: [] };
    }

    function getActionsColumn(item: ScorecardData, index: number) {
        const actions: ActionType[] = [];

        if ((userRoles.includes(SiteAdminRole) || userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole))) {
            actions.push(
                {
                    key: index.toString(),
                    label: 'Assign Users',
                    action: () => {
                        handleAssignUser(item.ID);
                    },
                    icon: {
                        color: 'primary',
                        name: 'person_add'
                    }
                }
            )
        }

        if (item.IsLocked) {
            actions.push({
                key: index.toString(),
                label: 'Locked',
                action: () => {},
                icon: {
                    color: 'primary',
                    name: 'lock'
                }
            });
        }
        else {
            actions.push({
                key: index.toString(),
                label: 'Unlocked',
                action: () => {},
                icon: {
                    color: 'primary',
                    name: 'lock_open'
                }
            });
        }

        return actions;
    }
}

